import { BulkUpdateTransactionsDrawer } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Transactions/BulkUpdateTransactionsDrawer/BulkUpdateTransactionsDrawer';
import { BulkUploadResultDrawer } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Transactions/BulkUploadResultDrawer/BulkUploadResultDrawer';
import { EditTransactionDrawer } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Transactions/EditTransactionDrawer/EditTransactionDrawer';
import { useTransactionsBulkAction } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Transactions/useTransactionsBulkAction';
import {
	CAP_TRANSACTION_QUICK_FILTERS,
	useTransactionsQuickFilters,
} from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Transactions/useTransactionsQuickFilters';
import { OneSchemaImporterModal } from '@dealstack/frontend/shared/components/OneSchema/OneSchemaImporterModal';

import { ConfirmDeleteDrawer } from '@dealstack/frontend/shared/components/ConfirmDeleteDrawer/ConfirmDeleteDrawer';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import { MantineTable } from '@dealstack/frontend/shared/components/Table/MantineTable/MantineTable';
import { getFinalSelectionCount } from '@dealstack/frontend/shared/components/Table/MantineTable/table-utils';
import { TooltipButton } from '@dealstack/frontend/shared/components/Table/MantineTable/TooltipButton/TooltipButton';
import { useTranslationCapManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { defaultTheme } from '@dealstack/frontend/shared/mechanism/styleguide/defaultTheme';
import { ManageCapTransactionsTableColumn } from '@dealstack/shared/lib/common/business/cap/manageCap';
import { Button, Flex, rem } from '@mantine/core';
import { memo, useEffect, useMemo, useState } from 'react';
import { CreateNewTransactionDrawer } from './CreateNewTransactionDrawer/CreateNewTransactionDrawer';
import { useTransactionsController } from './useTransactionsController';
import {
	bulkDeleteAlertDescription,
	singleDeleteAlertDescription,
	singleDeleteAlertTitle,
} from './transactionDeleteAlerts.utils';
import { Table } from '@tanstack/react-table';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2.tsx';
import { ReorderTransactionsModal } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Transactions/ReorderTransactionsModal/ReorderTransactionsModal.tsx';
import { SplitTransactionDrawer } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Transactions/SplitTransactionDrawer/SplitTransactionDrawer.tsx';
import { PairTransactionDrawer } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Transactions/PairTransactionDrawer/PairTransactionDrawer.tsx';
import { generatePath, useNavigate } from 'react-router-dom';
import { GetTransactionRes, OneSchemaTemplateKey } from '@dealstack/shared/lib/generated';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds.ts';
import { PEOPLE_ROUTES } from '@dealstack/frontend/app/pages/Routing/routes.ts';
import { useBreadcrumbs } from '@dealstack/frontend/shared/components/Breadcrumbs/useBreadcrumbs.tsx';
import { DIRECTORY_BREADCRUMBS } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/shared/directoryBreadcrumbs.tsx';
import { InlineLoader } from '@dealstack/frontend/shared/components/loaders/InlineLoader/InlineLoader.tsx';
import { useTableColPreferences } from '@dealstack/frontend/shared/hooks/useTableColPreferences/useTableColPreferences.hook.ts';
import { StorageKeys } from '@dealstack/frontend/shared/helper/storageUtils.ts';
import { headerHeight } from '@dealstack/frontend/shared/mechanism/styleguide/GlobalStyling';
import { createBulkActions } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Transactions/createBulkActions.ts';
import { EmptyState } from '@dealstack/frontend/shared/components/EmptyState/EmptyState.tsx';
import { getUserFacingErrorMessage } from '@dealstack/frontend/shared/dataExchange/ApiLayer/apiLayer';

interface Props {
	selectedEntityLoading?: boolean;
}

const Transactions = ({ selectedEntityLoading }: Props) => {
	const tCapManage = useTranslationCapManage();
	const { columnVisibility, handleColumnVisibilityChange } = useTableColPreferences({
		storageKey: StorageKeys.CAP_TRANSACTIONS_TABLE_COLS,
		defaultColumnVisibility: {
			[ManageCapTransactionsTableColumn.id]: false,
			[ManageCapTransactionsTableColumn.soldForIds]: false,
			[ManageCapTransactionsTableColumn.boughtForIds]: false,
			[ManageCapTransactionsTableColumn.date]: true,
			[ManageCapTransactionsTableColumn.securityIds]: true,
			[ManageCapTransactionsTableColumn.types]: true,
			[ManageCapTransactionsTableColumn.sourceIds]: true,
			[ManageCapTransactionsTableColumn.destinationIds]: true,
			[ManageCapTransactionsTableColumn.amount]: true,
			[ManageCapTransactionsTableColumn.price]: true,
			[ManageCapTransactionsTableColumn.sellerMipIds]: false,
			[ManageCapTransactionsTableColumn.buyerMipIds]: false,
		},
	});

	const {
		dealId,
		selectedEntityName,
		selectedEntityRegNumber,
		selectedEntityId,
		dataOperations,
		createOneSchemaSessionRequest,
		onBulkUploadTransactions,
		oneSchemaConfig,
		getTransactionsListRequest,
		transactionsListRefetch,
		securities,
		isLoading,
		isTableLoading,
		mantineTableDataOperations,
		columns,
		onDelete,
		showOneSchemaModal,
		setShowOneSchemaModal,
		showCreateNewTransactionDrawer,
		showEditTransactionDrawer,
		setShowEditTransactionDrawer,
		showSplitTransactionDrawer,
		showPairTransactionDrawer,
		setShowPairTransactionDrawer,
		setShowSplitTransactionDrawer,
		setShowCreateNewTransactionDrawer,
		transactionToEdit,
		setTransactionToEdit,
		onCreateTransactionModalClose,
		tableRef,
		showTable,
		showBulkUploadResultDrawer,
		setShowBulkUploadResultDrawer,
		bulkUploadTransactionsRequest,
		deleteTransactionRequest,
		refreshCapDetails,
		refetchPersons,
		statusResponse,
		transactionToReorder,
		pairTransaction,
		editTransaction,
		showSplitTransaction,
		setTransactionToReorder,
		showDeletedTable,
		selectedEntityData,
		toggleDeletedView,
		hasEditorPermission,
		hasAdminPermission,
		hasExportViewerPermission,
		deletedTransactionCount,
	} = useTransactionsController();

	const {
		bulkDeleteTransactions,
		deleteTransactionsRequest,
		confirmBulkDeleteOpened,
		setConfirmBulkDeleteOpened,
		bulkUpdateOpened,
		setBulkUpdateOpened,
		bulkUpdateField,
		bulkUpdateTransactions,
		updateTransactionsRequest,
		initiateBulkDelete,
		initiateBulkUpdate,
		initiateBulkRestore,
		bulkOperationParams,
		isThereInvestmentWorkflow,
		tsaNumber,
		pairedLegsIncluded,
		userIsAdmin,
		downloadExcel,
	} = useTransactionsBulkAction(
		transactionsListRefetch,
		refreshCapDetails,
		refetchPersons,
		dataOperations
	);

	const navigate = useNavigate();
	const [confirmDeleteOpened, setConfirmDeleteOpened] = useState<boolean>(false);
	// The row the single-delete confirmation is about. Held here rather than read from
	// `transactionToEdit`, which the effect above clears on the same tick it is set (the row click
	// navigates to the details page), so anything derived from it was stale by the time this drawer
	// opened — `tsaConnected` included, which left it permanently in its "forbidden" state.
	const [transactionToDelete, setTransactionToDelete] = useState<GetTransactionRes | undefined>();
	const { quickFilters, handleQuickFilter, quickFilter, appliedFilterLabels } =
		useTransactionsQuickFilters(mantineTableDataOperations);
	const [forceDelete, setForceDelete] = useState(false);

	const [oneSchemaSessionId, setOneSchemaSessionId] = useState<string | null>(null);

	const [topPosition, setTopPosition] = useState(headerHeight);

	const showDeleteTable = () => {
		toggleDeletedView(true);
	};

	const returnToTable = () => {
		toggleDeletedView(false);
	};

	useEffect(() => {
		if (transactionToEdit) {
			const path = generatePath(`..${PEOPLE_ROUTES.TRANSACTION_DETAILS}`, {
				transactionId: transactionToEdit.id,
			});
			navigate(path, {
				state: {
					navigationSource: true,
				},
			});

			setTransactionToEdit(undefined);
		}
	}, [transactionToEdit, navigate, setTransactionToEdit]);
	const tsaConnected = useMemo(
		() =>
			!!transactionToDelete &&
			(transactionToDelete.sourcePgTsaId !== null ||
				transactionToDelete.destinationPgTsaId !== null) &&
			!forceDelete,
		[transactionToDelete, forceDelete]
	);

	const quickFilterButtons = quickFilters.map(({ title, filter, dataTestId }) => (
		<TooltipButton
			key={filter}
			text={title}
			active={quickFilter === filter}
			onClick={() => {
				if (quickFilter === filter) {
					handleQuickFilter(CAP_TRANSACTION_QUICK_FILTERS.NONE);
				} else {
					handleQuickFilter(filter);
				}
			}}
			data-testid={dataTestId}
			tooltipLabel={title}
			style={
				filter === CAP_TRANSACTION_QUICK_FILTERS.INVALID ? { width: '7.125rem' } : undefined
			}
		/>
	));

	const [oneSchemaLoading, setOneSchemaLoading] = useState(false);

	const openOneSchemaModal = () => {
		setOneSchemaLoading(true);

		if (!oneSchemaConfig?.complex) {
			setShowOneSchemaModal(true);
			return;
		}

		createOneSchemaSessionRequest.mutate(
			{
				selectedEntityId,
				dealId,
				oneSchemaJwt: oneSchemaConfig?.jwt,
				oneSchemaClientId: oneSchemaConfig?.clientId,
				oneSchemaTemplateKey: OneSchemaTemplateKey.CAP_TRANSACTIONS_UPLOAD_COMPLEX,
			},
			{
				onSuccess: (response) => {
					if (!response || response.error) {
						showToastV2({
							state: 'error',
							title: response ? response.error : '',
							message: response?.message || 'Failed to create one schema session',
						});
						setOneSchemaLoading(false);
						return;
					}

					setOneSchemaSessionId(response?.token || null);
					setShowOneSchemaModal(true);
				},
				onError: (error) => {
					const message = getUserFacingErrorMessage(error);
					if (message) {
						showToastV2({
							state: 'error',
							message,
						});
					}
					setOneSchemaLoading(false);
				},
			}
		);
	};

	const breadcrumbs = useMemo(
		() =>
			DIRECTORY_BREADCRUMBS[PEOPLE_ROUTES.TRANSACTIONS]({
				tCapManage,
				dealId,
				personId: selectedEntityId,
				personDisplayName: selectedEntityData?.displayName,
				noLink: true,
			}),
		[tCapManage, dealId, selectedEntityId, selectedEntityData]
	);

	useBreadcrumbs(breadcrumbs);

	const deleteTransaction = () => {
		if (transactionToDelete) {
			onDelete(transactionToDelete.id);
		}

		if (transactionToEdit) {
			setTransactionToEdit(undefined);
		}
	};

	const handleBulkDelete = () => {
		void bulkDeleteTransactions(forceDelete);
	};

	return (
		<Flex h="100%" direction="column" p="1rem" gap="1rem">
			<Flex justify="flex-end" gap="0.5rem">
				{hasEditorPermission && (
					<>
						<Button
							onClick={openOneSchemaModal}
							loading={oneSchemaLoading}
							key="uploadCSV"
							variant="default"
							leftSection={<Icon variant="upload-v3-thin" size="lg" />}
							disabled={!selectedEntityData?.hasSecurities}
						>
							{tCapManage('transactions.header_actions.upload_transaction')}
						</Button>
						{hasAdminPermission && !showTable && deletedTransactionCount > 0 && (
							<Button
								onClick={showDeleteTable}
								key="viewDeleted"
								variant="default"
								leftSection={<Icon variant="trash-can" size="lg" />}
							>
								{tCapManage('table.bulk_actions.show_deleted')}
							</Button>
						)}
						<Button
							onClick={() => {
								setShowCreateNewTransactionDrawer(true);
							}}
							data-testid={TEST_ELEMENT_IDS.CAP_ADD_TRANSACTION_BUTTON}
							key="addTransaction"
							leftSection={<Icon variant="add" size="lg" />}
							disabled={!selectedEntityData?.hasSecurities}
						>
							{tCapManage('transactions.header_actions.add_transaction')}
						</Button>
					</>
				)}
			</Flex>
			{(selectedEntityLoading || isLoading) && <InlineLoader />}
			{showTable ? (
				<MantineTable<GetTransactionRes>
					dataTestId={TEST_ELEMENT_IDS.CAP_TRANSACTION_TABLE}
					getRowId={(row) => row.id}
					renderBulkActionsMenu={hasExportViewerPermission}
					enablePagination={true}
					manualPagination={true}
					enableColumnResizing={true}
					pinnedColumnIds={[
						ManageCapTransactionsTableColumn.id,
						ManageCapTransactionsTableColumn.date,
					]}
					actions={(table: Table<GetTransactionRes>) =>
						createBulkActions(table, {
							showDeletedTable,
							isLoading,
							hasEditorPermission,
							hasAdminPermission,
							userIsAdmin,
							selectedEntityId,
							dealId,
							tCapManage,
							initiateBulkRestore,
							downloadExcel,
							initiateBulkUpdate,
							initiateBulkDelete,
							setForceDelete,
							returnToTable,
							showDeleteTable,
							dataOperations,
						})
					}
					mantineTableBodyRowProps={({ row }) => ({
						id: row.id,
						style: {
							cursor: showDeletedTable ? 'default' : 'pointer',
							// Apply conditional styling for invalid transactions
							...(!showDeletedTable &&
								!row.original.isTransactionValid && {
									borderBottom: `1px solid ${defaultTheme.v3.colors.red[2]}`,
									backgroundColor: defaultTheme.v3.colors.red[0],
								}),
						},
						onClick: () => {
							if (showDeletedTable) {
								return;
							}
							setTransactionToEdit(row.original);
						},
					})}
					tableRef={tableRef}
					customFilters={quickFilterButtons}
					appliedFilterLabels={appliedFilterLabels}
					columns={columns}
					isLoading={isTableLoading}
					dataOperations={mantineTableDataOperations}
					data={
						showDeletedTable
							? getTransactionsListRequest.data?.deletedTransactions || []
							: getTransactionsListRequest.data?.transactions || []
					}
					rowCount={
						showDeletedTable
							? getTransactionsListRequest.data?.totalCountDeleted || 0
							: getTransactionsListRequest.data?.totalCount || 0
					}
					showDeletedTable={showDeletedTable}
					enableRowActions={false}
					enableStickyHeader={true}
					enableStickyFooter={true}
					returnToTable={returnToTable}
					initialState={{
						columnVisibility,
					}}
					state={{
						columnVisibility,
					}}
					onColumnVisibilityChange={handleColumnVisibilityChange}
					onIsFullScreenChange={(isFullscreen) => {
						if (isFullscreen) {
							setTopPosition(rem('48px'));
						} else {
							setTopPosition(headerHeight);
						}
					}}
				/>
			) : (
				<EmptyState
					icon="es-piled-disks"
					title={tCapManage('transactions.initial_page.title')}
					description={tCapManage('transactions.initial_page.description')}
				>
					{hasEditorPermission && (
						<Flex align="center" gap="0.5rem">
							<Button
								onClick={openOneSchemaModal}
								disabled={oneSchemaLoading || !selectedEntityData?.hasSecurities}
								variant="default"
								leftSection={<Icon variant="upload-v3-thin" size="lg" />}
							>
								{tCapManage('transactions.initial_page.upload_transaction')}
							</Button>

							<Button
								onClick={() => {
									setShowCreateNewTransactionDrawer(true);
								}}
								leftSection={<Icon variant="add" size="lg" />}
								disabled={!selectedEntityData?.hasSecurities}
							>
								{tCapManage('transactions.initial_page.add_transaction')}
							</Button>
						</Flex>
					)}
				</EmptyState>
			)}
			<CreateNewTransactionDrawer
				opened={showCreateNewTransactionDrawer}
				onClose={onCreateTransactionModalClose}
				refreshTransactionsList={transactionsListRefetch}
				securities={securities}
			/>
			<EditTransactionDrawer
				opened={showEditTransactionDrawer}
				onClose={() => {
					setTransactionToEdit(undefined);
					setShowEditTransactionDrawer(false);
				}}
				refreshTransactionsList={transactionsListRefetch}
				securities={securities}
				transaction={editTransaction || ({} as GetTransactionRes)}
				onDeletePair={() => {
					if (!editTransaction) {
						return;
					}
					setTransactionToDelete(editTransaction);
					setForceDelete(false);
					setShowEditTransactionDrawer(false);
					setConfirmDeleteOpened(true);
				}}
			/>
			<SplitTransactionDrawer
				opened={showSplitTransactionDrawer}
				onClose={() => {
					setTransactionToEdit(undefined);
					setShowSplitTransactionDrawer(false);
				}}
				transaction={showSplitTransaction || ({} as GetTransactionRes)}
				selectedEntityName={selectedEntityName}
				selectedEntityRegNumber={selectedEntityRegNumber}
				refreshTransactionsList={transactionsListRefetch}
			/>
			<PairTransactionDrawer
				opened={showPairTransactionDrawer}
				onClose={() => {
					setTransactionToEdit(undefined);
					setShowPairTransactionDrawer(false);
				}}
				transaction={pairTransaction || ({} as GetTransactionRes)}
				securitiesNames={securities}
				selectedEntityName={selectedEntityName}
				selectedEntityRegNumber={selectedEntityRegNumber}
				refreshTransactionsList={transactionsListRefetch}
			/>
			{transactionToReorder?.date && (
				<ReorderTransactionsModal
					personId={selectedEntityId}
					opened={!!transactionToReorder}
					listRefetch={transactionsListRefetch}
					onClose={() => setTransactionToReorder(null)}
					transactionDate={transactionToReorder?.date}
					onRefreshParent={() => {
						setTransactionToReorder(null);
						transactionsListRefetch();
					}}
					topPosition={topPosition}
					entityCurrency={selectedEntityData?.entityCurrency}
					initialTitles={dataOperations.filter?.titles}
				/>
			)}
			<BulkUpdateTransactionsDrawer
				opened={!!bulkUpdateField && bulkUpdateOpened}
				onClose={() => {
					setBulkUpdateOpened(false);
				}}
				updateField={bulkUpdateField!}
				onSubmit={bulkUpdateTransactions}
				isSubmitting={updateTransactionsRequest.isPending}
				transactionTotalCount={getTransactionsListRequest.data?.totalCount}
				selectedCount={
					bulkOperationParams?.table
						? getFinalSelectionCount(bulkOperationParams?.table)
						: undefined
				}
			/>
			<BulkUploadResultDrawer
				rowErrors={statusResponse?.rowErrors}
				systemError={statusResponse?.systemError}
				opened={showBulkUploadResultDrawer}
				onClose={() => setShowBulkUploadResultDrawer(false)}
				createdEntitiesNumber={bulkUploadTransactionsRequest.data?.createdEntitiesCount}
				status={statusResponse?.status}
				progressPercentage={(Number(statusResponse?.progress) || 0) * 100}
				totalProcessed={statusResponse?.totalProcessed || 0}
				total={statusResponse?.total || 0}
			/>
			<ConfirmDeleteDrawer
				opened={confirmDeleteOpened}
				onClose={() => {
					setConfirmDeleteOpened(false);
					setTransactionToDelete(undefined);
				}}
				title={tCapManage('transaction.delete.drawer.title')}
				alertTitle={singleDeleteAlertTitle(
					{ tsaConnected, pairedLeg: !!transactionToDelete?.interestPairId },
					tCapManage
				)}
				alertDescription={singleDeleteAlertDescription(
					{ tsaConnected, pairedLeg: !!transactionToDelete?.interestPairId },
					tCapManage
				)}
				inputLabel={tCapManage('delete.transaction.drawer.single.inputLabel')}
				inputPlaceholder={tCapManage('delete.transaction.drawer.single.inputPlaceholder')}
				hideAlertIcon={true}
				confirmDeleteText={tCapManage('delete.transaction.drawer.single.confirmDeleteWord')}
				wordMismatchErrorMessage={tCapManage(
					'delete.transaction.drawer.single.wordMismatchError'
				)}
				deleteBtnText={tCapManage('delete.transaction.drawer.confirmDelete')}
				cancelBtnText={tCapManage('delete.transaction.drawer.cancel')}
				deleteSuccess={deleteTransactionRequest.isSuccess}
				deleteLoading={deleteTransactionRequest.isPending}
				handleDelete={deleteTransaction}
				tsaConnected={tsaConnected}
			/>
			<ConfirmDeleteDrawer
				opened={confirmBulkDeleteOpened}
				onClose={() => setConfirmBulkDeleteOpened(false)}
				title={tCapManage('transaction.delete.drawer.title')}
				alertTitle={
					isThereInvestmentWorkflow
						? tCapManage('delete.transaction.drawer.bulk.forbiddenTitle')
						: tCapManage('delete.transaction.drawer.bulk.alertTitle')
				}
				alertDescription={bulkDeleteAlertDescription(
					{ investmentWorkflowIncluded: isThereInvestmentWorkflow, pairedLegsIncluded },
					tCapManage
				)}
				additionalInfo={tCapManage('delete.transaction.drawer.bulk.forbiddenNumber')}
				tsaNumber={tsaNumber}
				inputLabel={tCapManage('delete.transaction.drawer.bulk.inputLabel')}
				inputPlaceholder={tCapManage('delete.transaction.drawer.bulk.inputPlaceholder')}
				hideAlertIcon={true}
				confirmDeleteText={tCapManage('delete.transaction.drawer.bulk.confirmDeleteWord')}
				wordMismatchErrorMessage={tCapManage(
					'delete.transaction.drawer.bulk.wordMismatchError'
				)}
				deleteBtnText={tCapManage('delete.transaction.drawer.confirmDelete')}
				cancelBtnText={tCapManage('delete.transaction.drawer.cancel')}
				deleteSuccess={deleteTransactionsRequest.isSuccess}
				deleteLoading={deleteTransactionsRequest.isPending}
				handleDelete={handleBulkDelete}
			/>
			{oneSchemaConfig?.clientId && (
				<OneSchemaImporterModal
					isOpen={showOneSchemaModal}
					onRequestClose={() => setShowOneSchemaModal(false)}
					onSuccess={(response) =>
						onBulkUploadTransactions(response, oneSchemaSessionId || '')
					}
					onLaunched={(launchArgs) => {
						setOneSchemaLoading(false);
						setOneSchemaSessionId(launchArgs.sessionToken || null);
					}}
					clientId={oneSchemaConfig?.clientId}
					userJwt={oneSchemaConfig?.jwt}
					templateKey={oneSchemaConfig.template}
					devMode={process.env.NODE_ENV !== 'production'}
					importConfig={{ type: 'local' }}
				/>
			)}
		</Flex>
	);
};

const Memoized = memo(Transactions);

export { Memoized as Transactions };
